import { BodyPrimary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useState } from 'react';
import {
    CloseButton,
    ModalActions,
    ModalContent,
    ModalHeader,
    ModalTitle,
} from '@liveauctioneers/hammer-ui-core/modal/components';
import { Distance } from '@/types/Distance';
import { Drawer } from '@liveauctioneers/hammer-ui-core/drawer';
import { LocationFormContent } from './components/LocationFormContent';
import { Modal } from '@liveauctioneers/hammer-ui-core/modal';
import { OptionType, Select } from '@liveauctioneers/hammer-ui-core/select';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { US_POSTAL_CODE_LENGTH, US_ZIP_CODE_REGEX } from '@/utils/address';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useWindowIsMobile } from '@liveauctioneers/hammer-ui-core/providers';

const DISTANCE_OPTIONS = [20, 40, 60, 80, 100, 120, 250, 500, 1000];
const distanceOptions: OptionType[] = DISTANCE_OPTIONS.map((value) => ({
    label: `${value} miles`,
    value,
}));

type Props = {
    defaultZipCode?: string;
    distance: Distance;
    onApply: (zipCode: string, distanceValue: number) => void;
    onClose: () => void;
    show: boolean;
};

export const UpdateLocationModal = ({ defaultZipCode, distance, onApply, onClose, show }: Props) => {
    const { formatMessage } = useIntl();
    const isMobile = useWindowIsMobile();

    const [selectedDistance, setSelectedDistance] = useState<OptionType>(
        () => distanceOptions.find((opt) => opt.value === distance?.space) ?? distanceOptions[0]
    );
    const [zipCode, setZipCode] = useState(() => distance?.zipCode ?? defaultZipCode ?? '');

    useEffect(() => {
        if (show) {
            setSelectedDistance(distanceOptions.find((opt) => opt.value === distance?.space) ?? distanceOptions[0]);
            setZipCode(distance?.zipCode ?? defaultZipCode ?? '');
        }
    }, [show, distance?.space, distance?.zipCode, defaultZipCode]);

    const handleApply = useCallback(() => {
        const trimmed = zipCode.trim();
        if (!trimmed.length) {
            return;
        }
        if (!US_ZIP_CODE_REGEX.test(trimmed)) {
            toast('warning', { title: formatMessage({ id: 'searchPage.facets.validZipCodeRequired' }) });
            return;
        }
        onApply(trimmed.slice(0, US_POSTAL_CODE_LENGTH), Number(selectedDistance.value));
    }, [formatMessage, zipCode, selectedDistance, onApply]);

    const subtitle = formatMessage({ id: 'searchPage.facets.locationModal.subtitle' });

    const formFields = (
        <>
            <div className="flex-1">
                <Select
                    aria-label={formatMessage({ id: 'searchPage.facets.locationModal.maxDistance' })}
                    labelText={formatMessage({ id: 'searchPage.facets.locationModal.maxDistance' })}
                    onChange={(option: OptionType) => {
                        setSelectedDistance(option);
                    }}
                    options={distanceOptions}
                    usePortalMenu
                    value={selectedDistance}
                />
            </div>
            <div className="flex-1">
                <TextInput
                    inputMode="numeric"
                    labelText={formatMessage({ id: 'searchPage.facets.locationModal.usZipCode' })}
                    maxLength={10}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const sanitized = e.target.value.replace(/[^\d-]/g, '');
                        setZipCode(sanitized);
                    }}
                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                            handleApply();
                        }
                    }}
                    placeholder={formatMessage({ id: 'searchPage.facets.locationModal.usZipCode' })}
                    value={zipCode}
                />
            </div>
        </>
    );

    if (isMobile) {
        return (
            <Drawer
                actions={
                    <div className="grid w-full grid-cols-2 gap-xs">
                        <Button
                            onClick={onClose}
                            variant="outlined"
                        >
                            {formatMessage({ id: 'searchPage.facets.locationModal.back' })}
                        </Button>
                        <Button onClick={handleApply}>
                            {formatMessage({ id: 'searchPage.facets.locationModal.apply' })}
                        </Button>
                    </div>
                }
                anchor="right"
                // Must stack above AllFilters drawer (z-[1001])
                className="z-[1002]"
                handleClose={onClose}
                headerText={formatMessage({ id: 'searchPage.facets.locationModal.title' })}
                show={show}
                size="wide"
            >
                <LocationFormContent subtitle={subtitle}>{formFields}</LocationFormContent>
            </Drawer>
        );
    }

    return (
        <Modal
            closeModalOnBackdropClick={false}
            handleClose={onClose}
            show={show}
            type="composable"
        >
            <CloseButton />
            <ModalHeader>
                <ModalTitle>
                    <H2>{formatMessage({ id: 'searchPage.facets.locationModal.title' })}</H2>
                </ModalTitle>
                <BodyPrimary className="mt-2xs">
                    {formatMessage({ id: 'searchPage.facets.locationModal.subtitle' })}
                </BodyPrimary>
            </ModalHeader>
            <ModalContent>
                <div className="flex flex-col gap-md desktop:flex-row">{formFields}</div>
            </ModalContent>
            <ModalActions>
                <Button
                    onClick={onClose}
                    variant="outlined"
                >
                    {formatMessage({ id: 'searchPage.facets.locationModal.cancel' })}
                </Button>
                <Button onClick={handleApply}>{formatMessage({ id: 'searchPage.facets.locationModal.apply' })}</Button>
            </ModalActions>
        </Modal>
    );
};
